import React, { useState, createRef } from 'react';
import { useQueryClient } from 'react-query';
import { MenuItem, Select, Checkbox} from '@material-ui/core';
import MaterialTable from "@material-table/core";
import {defaultComponents} from "../../../controls/Material-Table-Components";
import { TablePagination } from "@material-ui/core";

import _ from "underscore";

import { MaskedNumber, ConvertedNumber } from "../../../controls";
import CurrencyLookup from "../../Currencies/components/CurrencyLookup";
import UomLookup from "../../Settings/components/UomLookup";
import { useAddFuelFee, useUpdateFuelFee, useDeleteFuelFee, useFuelFees } from "../../../services/fuel";
import FeeType from "./FeeType";
import FuelFeesValidator from "../validators/FuelFeesValidator";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";

const FuelDetails = (props) => {

  const queryClient = useQueryClient();
  const tableRef = createRef();
  const fuelRateUuid = props.record.fuelRateUuid;

  const handleAddFuelFee = useAddFuelFee();
  const handleUpdateFuelFee = useUpdateFuelFee();
  const handleDeleteFuelFee = useDeleteFuelFee();

  const fuelFees = useFuelFees({ tableRef, queryClient, extraKeys:[fuelRateUuid] });

  const [state] = useState({
    canEditFuel: props.editFuelRoles,
  });

  const feeTypes = [
    {feeCode: "DIFFERENTIAL", name: "Differential"},
    {feeCode: "FEE", name: "Fees / Taxes"},
    {feeCode: "FIXED", name: "Fixed Fee"},
  ];

  const getFeeName = (feeCode) => {
    const fee = _.findWhere(feeTypes, {feeCode});
    if (fee) return fee.name;
    return "";
  };

  const getGroupTotal = (groupType, fees) => {
    if (!fees) return 0;
    const rows = _.where(fees, {groupType});

    let total = 0;
    rows.forEach(row => {
      total += row.dollarAmount;
    });

      return parseFloat(total.toFixed(3));
  }

  const updateTotals = (postUpdate) => {
    const fuelRate = props.record;
    const fees = queryClient.getQueryData(postUpdate.key);
    const totalDiffs = getGroupTotal("DIFFERENTIAL", fees);
    const totalFees = getGroupTotal("FEE", fees);
    const totalFixedFees = getGroupTotal("FIXED", fees);
    const {dollarPlattsPrice} = fuelRate;

    let rate = dollarPlattsPrice + totalDiffs + totalFees;

    const data = { totalDiffs, totalFees, totalFixedFees, rate };
    props.onChange(data);
  }

  const validatorColumn = ValidatorColumn(FuelFeesValidator, true, undefined, false);

  const fuelFeeColumns = [
    {...validatorColumn},
    {
      ...(ColumnWidth(180, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
      title: "Fee Type",
      field: "groupType",
      editable: state.canEditFuel ? "always" : "never",
      emptyValue: "",
      initialEditValue: "FEE",
      editComponent: (props) => (
          <Select
              labelId="Fee Type"
              id="Fee Type"
              value={props.value}
              onChange={e => {
                props.onChange(e.target.value);
              }}
              style={{ fontSize: "0.875rem"}}

          >
            {feeTypes.map((type) => {
              return (<MenuItem key={type.feeCode} value={type.feeCode}>{type.name}</MenuItem>);
              })
            }
          </Select>
      ),
      render: (rowData) => (<div>{getFeeName(rowData.groupType)}</div>),

    },
    {
      title: "Type",
      field: "type",
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: ((cell, rowData, updateData) => {return (updateData.groupType === "FEE" || updateData.groupType === "FIXED") }),
      emptyValue: "",
      initialEditValue: "",
      render: rowData => {
        if (rowData.groupType === "FEE" || rowData.groupType === "FIXED") {
          return (<div>{rowData.type}&nbsp;-&nbsp;{rowData.name}</div>);
        }
        return (<div></div>);
      },
      editComponent: (props) => (
        <FeeType
          type={props.value}
          name={props.rowData.name}
          defaultType="OTHER"
          style={{width: "100%"}}
          onChange={e => {
            let data = { ...props.rowData, type: e.type, name: e.name };
            props.onRowDataChange(data);
          }}
        />
      ),  
    },
    {
      ...(ColumnWidth(100)),
      title: "Currency",
      field: "currencyCode",
      editable: state.canEditFuel ? "always" : "never",
      emptyValue: "",
      initialEditValue: "USDC",
      editComponent: (props) => (
        <CurrencyLookup
          showChildCurrencies={true}
          value={props.value}
          onChange={props.onChange}
          style={{ fontSize: "0.875rem", textAlign: "right"}}
        />
      ),
    },
    {
      ...(ColumnWidth(140, undefined, undefined, {paddingRight: 20}, {paddingRight: 20})),
      title: "Amount",
      field: "amount",
      type: "numeric",
      editable: state.canEditFuel ? "always" : "never",
      emptyValue: 0,
      initialEditValue: 0, 
      editComponent: (props) => (
        <MaskedNumber
          value={props.value}
          decimalScale={3}
          fixedDecimalScale={true}
          onChange={props.onChange}
          style={{ fontSize: "0.875rem", textAlign: "right"}}
        />
      ),
    },
    {
      ...(ColumnWidth(140)),
      title: "Unit of Measure",
      field: "priceUom",
      editable: ((cell,rowData,updateData) => { return (updateData.groupType === "FEE" || updateData.groupType === "DIFFERENTIAL") }),
      emptyValue: "",
      initialEditValue: "USG",
      render: rowData => {
        if (rowData.groupType === "FEE" || rowData.groupType === "DIFFERENTIAL") {
          //const uom = _.findWhere(uomCodes, { uomCode: rowData.priceUom });
          const uomName = rowData.priceUom ? rowData.priceUom : "";
          return (<div>{uomName}</div>);
        }
        return (<div></div>);
      },
      editComponent: (props) => (
        <UomLookup
          value={props.value}
          onChange={props.onChange}
        />
      ),
    },
    {
      ...(ColumnWidth(90)),
      title: "Override",
      field: "overrideCalculation",
      editable: state.canEditFuel ? "always" : "never",
      emptyValue: false,
      initialEditValue: false, 
      type: "boolean",
      render: rowData => <Checkbox style={{padding: 0}} checked={rowData.overrideCalculation === true} value="true" />        
    },
    {
      ...(ColumnWidth(140, undefined, undefined, {paddingRight: 20}, {paddingRight: 20})),
      title: "Converted to $c /AG",
      field: "dollarAmount",
      type: "numeric",
      emptyValue: 0,
      initialEditValue: 0,
      editable: ((cell, rowData, updatedData) => {
        return updatedData.overrideCalculation;
      }),
      editComponent: (props) => (
        <MaskedNumber
          value={props.value}
          decimalScale={3}
          fixedDecimalScale={true}
          onChange={props.onChange}
          style={{ fontSize: "0.875rem", textAlign: "right"}}
        />
      ),

      render: (rowData, rowType, updatedData) => {
        if (updatedData) {
          const editing = tableRef && tableRef.current && tableRef.current.dataManager.lastEditingRow && tableRef.current.dataManager.lastEditingRow.fuelFeeUuid === updatedData.fuelFeeUuid;
          const adding = tableRef && tableRef.current && tableRef.current.state && tableRef.current.state.showAddRow && rowType === "group"
          const dontConvert = updatedData.overrideCalculation || (!editing && !adding);
          return (
            <ConvertedNumber
              displayType="text"
              value={updatedData.overrideCalculation ? updatedData.dollarAmount : updatedData.amount}
              convertedValue={updatedData.dollarAmount}
              showError={!adding && !editing && !updatedData.overrideCalculation}
              dontConvert={dontConvert}
              decimalScale={3}
              fromCurrencyCode={updatedData.currencyCode}
              rateProviderCode={props.record.exchangeRateType}
              toCurrencyCode="USDC"
              rateDate={props.record.validFrom}
              prefix="$c "
              fixedDecimalScale={true}
              uom={updatedData.priceUom}
              onChange={(convertedValue) => {
                if (tableRef && tableRef.current && tableRef.current.dataManager.lastEditingRow) {
                  if (tableRef.current.dataManager.lastEditingRow.fuelFeeUuid === updatedData.fuelFeeUuid) {
                    updatedData.dollarAmount = convertedValue;
                  }
                }
                if (tableRef && tableRef.current && tableRef.current.state.showAddRow && rowType === "group") {
                  updatedData.dollarAmount = convertedValue;
                }
              }}
              style={{ fontSize: "0.875rem", textAlign: "right"}}
            />);
        } else
          return 0.00;
      },
    },
  ];

  return (
    <div style={{
      marginLeft: 50,
    }}>
      <MaterialTable
        tableRef={tableRef}
        style={{
          backgroundColor: "#fdfdfd",
          border: "2px solid #aaaaaa",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle:{
            backgroundColor: '#eaeaea',
            padding: "2px",
          },      
          toolbarButtonAlignment: "right",
          pageSize: 5,
          detailPanelType: "single",
          search: false,
        }}
        components={{
          ...defaultComponents,
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
        }}
        title="Fees and Surcharges"
        columns={fuelFeeColumns}
        data={fuelFees.data}
        editable={{
          isEditable: () => {
            return state.canEditFuel;
          },
          isDeletable: () => {
            return state.canEditFuel;
          },
          onRowAdd: state.canEditFuel
            ? (newData) => new Promise((resolve, reject) => {
              handleAddFuelFee.mutate({
                record: { fuelRateUuid, ...newData },
                resolve,
                reject,
                extraKeys: [fuelRateUuid],
                postSuccess: updateTotals,
                postError: updateTotals
              });
            })
            : undefined,
          onRowUpdate: (newData, oldData) =>
            new Promise((resolve, reject) => {
              handleUpdateFuelFee.mutate({
                record: newData,
                oldRecord: oldData,
                resolve, reject,
                extraKeys: [fuelRateUuid],
                postSuccess: updateTotals,
                postError: updateTotals
              });
            }),
          onRowDelete: (oldData) =>
            new Promise((resolve, reject) => {
              handleDeleteFuelFee.mutate({
                oldRecord: oldData,
                resolve, reject,
                extraKeys: [fuelRateUuid],
                postSuccess: updateTotals,
                postError: updateTotals
              });
            }),
        }}
      />
    </div>
  );
}

export default FuelDetails;
